import React from "react";
import { Chip } from "@mui/material";

/** Bytes → human string. Shared by the wizard, table, and details page. */
export const formatBytes = (b?: number | null): string => {
  if (!b) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1_048_576) return `${(b / 1024).toFixed(0)} KB`;
  if (b < 1_073_741_824) return `${(b / 1_048_576).toFixed(1)} MB`;
  return `${(b / 1_073_741_824).toFixed(2)} GB`;
};

export const fmtDate = (iso?: string | null): string =>
  iso ? new Date(iso).toLocaleString() : "—";

/** Every device update status, with its display label + colours. */
export const UPDATE_STATUS_META: Record<string, { label: string; bg: string; fg: string }> = {
  up_to_date:        { label: "Up to date",           bg: "#ecfdf5", fg: "#047857" },
  update_available:  { label: "Update available",     bg: "#eff6ff", fg: "#1d4ed8" },
  awaiting_approval: { label: "Awaiting approval",     bg: "#fef3c7", fg: "#92400e" },
  scheduled:         { label: "Scheduled",             bg: "#f5f3ff", fg: "#6d28d9" },
  pending:           { label: "Pending",               bg: "#f1f5f9", fg: "#475569" },
  downloading:       { label: "Downloading",           bg: "#eff6ff", fg: "#1d4ed8" },
  downloaded:        { label: "Downloaded",            bg: "#eef2ff", fg: "#4338ca" },
  installing:        { label: "Installing",            bg: "#eff6ff", fg: "#1d4ed8" },
  updating:          { label: "Updating",              bg: "#eff6ff", fg: "#1d4ed8" },
  installed:         { label: "Updated successfully",  bg: "#ecfdf5", fg: "#047857" },
  failed:            { label: "Installation failed",   bg: "#fef2f2", fg: "#b91c1c" },
  offline:           { label: "Device offline",        bg: "#f1f5f9", fg: "#64748b" },
  paused:            { label: "Update paused",         bg: "#fff7ed", fg: "#c2410c" },
  rolled_back:       { label: "Rolled back",           bg: "#fdf4ff", fg: "#a21caf" },
  superseded:        { label: "Superseded",            bg: "#f8fafc", fg: "#94a3b8" },
  cancelled:         { label: "Cancelled",             bg: "#f8fafc", fg: "#94a3b8" },
};

export const POLICY_META: Record<string, { label: string; bg: string; fg: string }> = {
  optional:  { label: "Optional",  bg: "#eff6ff", fg: "#1d4ed8" },
  automatic: { label: "Automatic", bg: "#f5f3ff", fg: "#6d28d9" },
  mandatory: { label: "Mandatory", bg: "#fef2f2", fg: "#b91c1c" },
};

export const CHANNEL_META: Record<string, { bg: string; fg: string }> = {
  stable: { bg: "#dcfce7", fg: "#166534" },
  beta:   { bg: "#fef3c7", fg: "#92400e" },
  test:   { bg: "#ede9fe", fg: "#5b21b6" },
};

export const UpdateStatusChip: React.FC<{ status?: string | null; size?: "small" }> = ({
  status, size = "small",
}) => {
  if (!status) return <span style={{ color: "#94a3b8", fontSize: 12 }}>—</span>;
  const m = UPDATE_STATUS_META[status] || { label: status, bg: "#f1f5f9", fg: "#475569" };
  return (
    <Chip
      size={size}
      label={m.label}
      sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: m.bg, color: m.fg }}
    />
  );
};

export const PolicyChip: React.FC<{ policy?: string | null }> = ({ policy }) => {
  if (!policy) return <span style={{ color: "#94a3b8", fontSize: 12 }}>—</span>;
  const m = POLICY_META[policy] || { label: policy, bg: "#f1f5f9", fg: "#475569" };
  return (
    <Chip size="small" label={m.label}
      sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: m.bg, color: m.fg }} />
  );
};

/** Deploy-To options for the upload wizard + release-details deploy dialog. */
export const DEPLOY_TARGETS = [
  { value: "none", label: "Do not deploy yet" },
  { value: "all_active", label: "All active devices" },
  { value: "companies", label: "Selected companies" },
  { value: "groups", label: "Selected device groups" },
  { value: "devices", label: "Selected devices" },
] as const;

export const POLICIES = [
  { value: "optional", label: "Optional", help: "Device reports availability; admin approves install later. Playback continues." },
  { value: "automatic", label: "Automatic", help: "Device downloads + installs during its maintenance window. Admin can Install Now." },
  { value: "mandatory", label: "Mandatory", help: "Device must update. Downloads automatically; installs as soon as the file is ready." },
] as const;

export const INSTALL_MODES = [
  { value: "immediate", label: "Install immediately" },
  { value: "scheduled", label: "Install at a specific date & time" },
  { value: "maintenance_window", label: "Install during device maintenance window" },
] as const;

export const ROLLOUT_STAGES = [
  { value: "test", label: "Test devices only", pct: 0 },
  { value: "5", label: "5%", pct: 5 },
  { value: "10", label: "10%", pct: 10 },
  { value: "25", label: "25%", pct: 25 },
  { value: "50", label: "50%", pct: 50 },
  { value: "100", label: "100% (all matching)", pct: 100 },
  { value: "custom", label: "Custom device selection", pct: 100 },
] as const;
